/**
 * App Providers — Query client, overlay stack, persona context, density controller.
 * Story 13.6 integration.
 */

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, type ReactNode } from "react";
import { OverlayStackProvider } from "@/shared/context/overlay-stack-context";
import { AnnouncementRegion } from "@/shared/components/a11y/AnnouncementRegion";
import { PersonaProvider } from "@/shared/context/persona-provider";
import { DensityController } from "@/shared/components/layout/DensityController";
import { useDensityStore } from "@/shared/store/density-store";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

interface ProvidersProps {
  children: ReactNode;
}

/**
 * Inner providers that require Router context (PersonaProvider uses useLocation).
 * Must be rendered inside BrowserRouter.
 */
export function RouteAwareProviders({ children }: ProvidersProps) {
  // Restore the user's saved density override from localStorage on boot —
  // the store otherwise starts every reload back at the persona default.
  useEffect(() => {
    useDensityStore.getState().hydrate();
  }, []);

  return (
    <PersonaProvider>
      <DensityController />
      {children}
    </PersonaProvider>
  );
}

/**
 * Top-level providers (no Router dependency).
 */
export function Providers({ children }: ProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <OverlayStackProvider>
        <AnnouncementRegion />
        {children}
      </OverlayStackProvider>
    </QueryClientProvider>
  );
}
